import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, HelpCircle, MessageSquare, DollarSign, Calendar, Shield } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const FAQsPage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: "About CeleyApp",
      icon: HelpCircle,
      questions: [
        {
          q: "How do you pronounce 'Celey'?",
          a: "CeleyApp is pronounced 'Cell-e App' – it's short for 'celebrate'! We help you celebrate life's special moments by connecting you with amazing service providers."
        },
        {
          q: "Is CeleyApp free to use?",
          a: "Yes. Creating an account and browsing service providers is completely free.\n\nFor service providers, CeleyApp charges a 3% platform fee only when a booking is successfully completed — providers keep 97% of their earnings. This fee is deducted from the provider's payout — there are no sign-up fees, no monthly fees, and no upfront costs.\n\nCustomers never pay extra platform fees."
        },
        {
          q: "How do I reset my password?",
          a: "Click 'Forgot Password?' on the login page and answer your security questions to reset your password."
        },
        {
          q: "Still need help?",
          a: "Email us at celeyapp@gmail.com with your question or concern, and we'll get back to you as soon as possible. We typically respond within 24-48 hours."
        }
      ]
    },
    {
      category: "Getting Started",
      icon: HelpCircle,
      questions: [
        {
          q: "How do I find a service provider?",
          a: "Click 'Browse Service Providers' to view all available service providers. You can search by category, location, or service type. Once you find someone you like, click 'Send Inquiry' to get started."
        },
        {
          q: "How do I become a service provider?",
          a: "It's easy and open to everyone! Sign up with a service provider account and complete your profile with your services and pricing. No LLC or business license is required — we welcome hobbyists, side-hustlers, and professionals alike. You don't even need a website — your CeleyApp profile becomes your online presence. Once your profile is complete, customers can start sending you inquiries."
        },
        {
          q: "Do I need a business license or LLC to be a service provider?",
          a: "No! CeleyApp is open to individuals at all levels — from hobbyists just starting out to experienced professionals. You don't need an LLC, business license, or even a website. Your CeleyApp profile serves as your professional online presence, and you can share your profile link anywhere as your business website."
        },
        {
          q: "How do I send an inquiry?",
          a: "Browse service providers, click on a profile, and click 'Request Quote' to send an inquiry. You must have a customer account to send inquiries."
        },
        {
          q: "Can service providers send inquiries?",
          a: "Service providers cannot send inquiries with their service provider account. Create a separate customer account if you'd like to book services."
        }
      ]
    },
    {
      category: "Messaging & Proposals",
      icon: MessageSquare,
      questions: [
        {
          q: "How does the messaging system work?",
          a: "When you send an inquiry, a private message thread opens between you and the service provider. You can discuss details, ask questions, and negotiate before receiving a formal proposal."
        },
        {
          q: "What happens after I send an inquiry?",
          a: "The service provider will receive a notification and respond via messages. They'll discuss your needs and then send you a formal proposal with a price. You can accept or decline the proposal."
        },
        {
          q: "Can I negotiate the price?",
          a: "Absolutely! Use the messaging system to discuss pricing with the service provider before they send a formal proposal. Once you're both agreed on terms, they'll send the official proposal."
        }
      ]
    },
    {
      category: "Payments & Deposits",
      icon: DollarSign,
      questions: [
        {
          q: "When do I pay?",
          a: "You pay in two steps: (1) A 15% deposit within 48 hours of signing the service agreement to secure the booking, and (2) The remaining 85% balance is due 7 days before your event date, or when the service provider marks the job complete."
        },
        {
          q: "How are payments processed?",
          a: "All payments are securely processed through Stripe. Your payment information is never stored on our servers. You can pay with credit/debit cards."
        },
        {
          q: "What if I need to cancel?",
          a: "You can cancel a booking from your dashboard. If you cancel within 48 hours of paying the deposit, you'll receive a full refund. After 48 hours, the deposit is retained by the service provider. If the service provider cancels, you'll always receive a full refund. Note: Service providers cannot cancel within 72 hours of your event date."
        },
        {
          q: "When does the service provider get paid?",
          a: "The service provider receives payment after the service is completed. Once the service provider marks the job complete, you'll be prompted to pay the final balance. They receive the full payment (minus a 3% platform fee) after you confirm completion."
        }
      ]
    },
    {
      category: "Service Agreements",
      icon: Shield,
      questions: [
        {
          q: "What is a service agreement?",
          a: "The service agreement is a simple contract that outlines both parties' responsibilities. Both you and the service provider must sign it after the deposit is paid before work begins."
        },
        {
          q: "Can I review the agreement before signing?",
          a: "Yes! You'll be prompted to review the full agreement before signing. Take your time to read through all terms and conditions."
        },
        {
          q: "What happens if there's a dispute?",
          a: "If any issues arise, first try to resolve them directly with the service provider via messages. If needed, you can contact our support team for assistance."
        }
      ]
    },
    {
      category: "Bookings & Calendar",
      icon: Calendar,
      questions: [
        {
          q: "How do I track my bookings?",
          a: "All your bookings are visible in your dashboard under the 'Inquiries' tab. You'll see the status, dates, and can communicate with your service provider there."
        },
        {
          q: "Can I book multiple services?",
          a: "Yes! You can send inquiries to multiple service providers and have multiple active bookings. Each booking is tracked separately in your dashboard."
        },
        {
          q: "What if the service provider isn't available on my date?",
          a: "Service providers manage their own availability calendars. If they can't accommodate your date, they'll let you know via messages and you can discuss alternative dates."
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen-safe bg-gradient-to-b from-gray-50 to-white">
      {/* Navigation */}
      <Navigation />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-blue-600 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <HelpCircle className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-emerald-50">
            Find answers to common questions about using CeleyApp
          </p>
        </div>
      </div>

      {/* FAQs Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {faqs.map((category, categoryIndex) => {
          const Icon = category.icon;
          return (
            <div key={categoryIndex} className="mb-12">
              {/* Category Header */}
              <div className="flex items-center mb-6">
                <div className="bg-emerald-100 p-3 rounded-lg mr-4">
                  <Icon className="h-6 w-6 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {category.category}
                </h2>
              </div>

              {/* Questions */}
              <div className="space-y-4">
                {category.questions.map((faq, questionIndex) => {
                  const isOpen = openIndex === `${categoryIndex}-${questionIndex}`;
                  return (
                    <Card 
                      key={questionIndex}
                      className="cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => toggleFAQ(categoryIndex, questionIndex)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-start">
                          <CardTitle className="text-lg font-semibold text-gray-900 pr-8">
                            {faq.q}
                          </CardTitle>
                          {isOpen ? (
                            <ChevronUp className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          )}
                        </div>
                      </CardHeader>
                      {isOpen && (
                        <CardContent className="pt-0">
                          <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                            {faq.a}
                          </p>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Still Have Questions Section */}
        <Card className="bg-gradient-to-r from-emerald-50 to-blue-50 border-emerald-200">
          <CardContent className="text-center py-12">
            <MessageSquare className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Still have questions?
            </h3>
            <p className="text-gray-600 mb-6">
              Our support team is here to help you
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              Contact Support
            </button>
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default FAQsPage;
